import React, { Fragment } from 'react';
import { Grid, Box, Typography, Divider, Chip, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  PaymentsOutlined, 
  CalendarMonthOutlined, 
  TimerOutlined, 
  SettingsSuggestOutlined 
} from '@mui/icons-material';

const LeavePolicyDetail = ({ item, index, variant = 'admin' }) => {
  const { t } = useTranslation();
  const isAdmin = variant === 'admin';

  // Helper Logic สำหรับการแสดงผล Can Request After
  const getCanRequestAfterLabel = (value) => {
    if (value === null) return t("StartWork");
    if (value < 365) return `${value} ${t("Days")}`;
    return `${t("YearOfService")} ${value / 365} ${t("Year")}`;
  };

  // Helper Logic สำหรับ Minimum Leave
  const getMinLeaveLabel = (value) => {
    const mapping = { 100: t("FullDay"), 50: t("HalfDay"), 10: t("Hour"), 5: t("HalfHour") };
    return mapping[value] || "-";
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: '12px', bgcolor: '#fff' }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography fontSize="18px" fontWeight="600">{t("EmployeeGroup")} {index + 1}</Typography>
        </Grid>

        <InfoItem label={t("EmployeeType")} value={item.employeeTypeList.length === 0 ? t("All") : item.employeeTypeList.map(x => x.employeeTypeName).join(", ")} />
        <InfoItem label={t("PersonalLevel")} value={item.personnelLevelGroupList.length === 0 ? t("All") : item.personnelLevelGroupList.map(x => x.groupLevelName).join(", ")} />
        <InfoItem label={t("TotalLeaveDays")} value={item.isLeaveStep ? t("OnCondition") : (item.leaveValue === null ? t("Leave.Unlimited") : `${item.leaveValue} ${t("Days")}`)} />
        <InfoItem label={t("AccumulateNextRoundNotMoreThan")} value={item.isLeaveStep ? t("OnCondition") : (item.carryValue === null ? t("Leave.Unlimited") : `${item.carryValue} ${t("Days")}`)} />
        
        <InfoItem label={t("Pay")} value={`${item.isPaid? t("Yes"): t("No")} ${item.payPercentage !== null? `(${item.payPercentage}%)`: ""}`} />
        <InfoItem label={t("CanRequestLeaveAfter")} value={item.canRequestAfter === null? `${t("StartWork")}`: item.canRequestAfter < 365? ` ${item.canRequestAfter} ${t("Days")}`: `${t("YearOfService")} ${item.canRequestAfter / 365} ${t("Year")}`} />

        <InfoItem label={t("Gender")} value={item.gender || t("AllGender")} />
        <InfoItem label={t("MinimumLeaveAllowed")} value={item.minLeave === 100
            ? `${t("FullDay")}`
            : item.minLeave === 50
            ? `${t("HalfDay")}`
            : item.minLeave === 10
            ? `1 ${t("Hour")}`
            : item.minLeave === 5
            ? `${t("HalfHour")}`
            : "-"
          }
        />

        <InfoItem label={t("Prorate")} value={item.isProRate ? t("Yes") : t("No")}/>
        <InfoItem label={`${t("Rounding")} Prorate`} value={item.proRateRound === 1
          ? t("ThereIsNoRounding")
          : item.proRateRound === 0.5
          ? `${t("RoundWhenGreaterThan")} 0.5`
          : item.proRateRound === 0
          ? t("RoundUpInAllCases")
          : "-"
        }/>
        
        <InfoItem label={t("LeaveIn")} value={item.dayInAdvance ? `${item.dayInAdvance} ${t("Days")}` : t("NotSpecified")} />

        {Boolean(item.isLeaveStep) && (
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography color="text.secondary">{t("Condition")}</Typography>
              </Grid>

              {item.leaveStepList.map((step, stepIndex) => (
                <Fragment key={`${item.idLeaveType}_${stepIndex}`}>
                  <Grid item xs={12} md={4}>
                    <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("YearOfService")}</Typography>
                    <Typography>
                      {`${step.lessThanYear}${
                        step.moreThanYears === 99
                          ? " ปีขึ้นไป"
                          : ` - ${step.moreThanYears} ${t("Year")}`
                      }`}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("TotalLeaveDays")}</Typography>
                    <Typography>{step.leaveValue !== null? `${step.leaveValue} ${t("Days")}`: t("Leave.Unlimited")}</Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography paddingBottom="4px" color="text.secondary" fontSize="14px">{t("AccumulateNextRoundNotMoreThan")}</Typography>
                    <Typography>{step.carryValue !== null? `${step.carryValue} ${t("Days")}`: t("Leave.Unlimited")}</Typography>
                  </Grid>
                </Fragment>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

const InfoItem = ({ label, value, highlight = false }) => (
  <Grid item xs={12} md={6}>
    <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: highlight ? 'primary.main' : 'text.primary', fontWeight: highlight ? 700 : 500 }}>
      {value || '-'}
    </Typography>
  </Grid>
);

export default LeavePolicyDetail;